import React, { useEffect, useState } from "react";
import { Box, Grid, MenuItem, Typography, IconButton } from "@mui/material";
import { Close as CloseIcon, Add as AddIcon } from "@mui/icons-material";
import { useNavigate, useParams, useLocation } from "react-router-dom";

import CustomSelect from "../../components/customSelect";
import CustomDatePicker from "../../components/datePicker";
import TextInput from "../../components/textInput";
import CustomButton from "../../components/customButton";
import CustomHeading from "../../components/customHeading";
import AppLoader from "../../components/appLoader";
import CustomInputLabel from "../../components/customInputLabel";
import CustomMultiSelect from "../../components/customMultiSelect";

import { openSnackAlert, uploadMediaService } from "../../helper";
import {
  createTask,
  updateTask,
  getTaskDetail,
} from "../../services/TaskManagement";
import {
  validateTaskForm,
  validateFileUpload,
} from "../../utils/taskValidation";
import {
  getInitialFormState,
  getInitialMaterialOption,
  transformTaskDataForForm,
  transformFormDataForAPI,
  handleFileUpload,
} from "../../utils/taskFormUtils";

import useUserStore from "../../zustand/useUserStore";
import useWorkerStore from "../../zustand/useWorkerStore";

const AddEditTask = () => {
  const { user } = useUserStore();
  const { allWorkers, initializeData } = useWorkerStore();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  const [form, setForm] = useState(getInitialFormState());
  const [isLoading, setIsLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    initializeData();
    if (id) {
      setIsEditMode(true);
      const taskFromLocation = location.state?.taskData;
      if (taskFromLocation) {
        setForm(transformTaskDataForForm(taskFromLocation));
      } else {
        loadTaskData();
      }
    }
  }, [id, location.state, initializeData]);

  const loadTaskData = async () => {
    try {
      const response = await getTaskDetail(id);
      const taskRes = response?.data;

      if (taskRes?.status === "success" && taskRes.data) {
        setForm(transformTaskDataForForm(taskRes.data));
      } else {
        openSnackAlert("Task not found", "error");
        navigate("/task-management");
      }
    } catch (error) {
      openSnackAlert("Something went wrong", "error");
    }
  };

  const handleChangeFile = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file
    const fileValidation = validateFileUpload(file);
    if (!fileValidation.isValid) {
      const errorMessage = Object.values(fileValidation.errors)[0];
      openSnackAlert(errorMessage, "error");
      event.target.value = "";
      return;
    }

    // Handle file upload
    const success = await handleFileUpload(
      file,
      form,
      setForm,
      setIsLoading,
      openSnackAlert
    );
    if (success) {
      event.target.value = "";
    }
  };

  const handleRemoveFile = () => {
    setForm((prev) => ({
      ...prev,
      attachments: null,
    }));
    openSnackAlert("File removed successfully", "success");
  };

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    // Validate files
    const maxSize = 5 * 1024 * 1024; // 5MB per image
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        openSnackAlert("Only JPEG, PNG, and WebP images are allowed", "error");
        event.target.value = "";
        return;
      }
      if (file.size > maxSize) {
        openSnackAlert("Image size should be less than 5MB", "error");
        event.target.value = "";
        return;
      }
    }

    // Check total images limit
    if (form.taskImages.length + files.length > 10) {
      openSnackAlert("Maximum 10 images allowed", "error");
      event.target.value = "";
      return;
    }

    try {
      setIsLoading(true);
      const uploadPromises = files.map((file) => uploadMediaService(file));
      const uploadedUrls = await Promise.all(uploadPromises);

      setForm((prev) => ({
        ...prev,
        taskImages: [...prev.taskImages, ...uploadedUrls],
      }));

      openSnackAlert(
        `${files.length} image(s) uploaded successfully`,
        "success"
      );
    } catch (error) {
      openSnackAlert("Failed to upload images", "error");
    } finally {
      setIsLoading(false);
      event.target.value = "";
    }
  };

  const handleRemoveImage = (indexToRemove) => {
    setForm((prev) => ({
      ...prev,
      taskImages: prev.taskImages.filter((_, index) => index !== indexToRemove),
    }));
    openSnackAlert("Image removed successfully", "success");
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleMultiSelectChange = (name, value) => {
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleDateChange = (field, date) => {
    setForm((prev) => ({ ...prev, [field]: date }));
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleTaskCategoryChange = (e) => {
    const selectedCategory = e.target.value;
    setForm((prev) => ({
      ...prev,
      taskCategory: selectedCategory,
      ...(selectedCategory === "landscape"
        ? { attachments: null, subTasks: [] }
        : {}),
    }));
  };

  const handleMaterialOptionChange = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      materialOption: prev.materialOption.map((row, i) =>
        i === index ? { ...row, [field]: value } : row
      ),
    }));

    if (validationErrors.materialOption) {
      setValidationErrors((prev) => ({ ...prev, materialOption: "" }));
    }
  };

  const handleAddMaterialRow = () => {
    setForm((prev) => ({
      ...prev,
      materialOption: [
        ...prev.materialOption,
        ...getInitialMaterialOption(),
      ],
    }));
  };

  const handleRemoveMaterialRow = (index) => {
    setForm((prev) => {
      const updated = prev.materialOption.filter((_, i) => i !== index);
      return {
        ...prev,
        materialOption:
          updated.length > 0 ? updated : getInitialMaterialOption(),
      };
    });
  };

  const handleSubmit = async () => {
    const validation = validateTaskForm(form);
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      const firstError = Object.values(validation.errors)[0];
      openSnackAlert(firstError, "error");
      return;
    }

    try {
      setIsLoading(true);
      const payload = transformFormDataForAPI(form, user?._id);

      const response = isEditMode
        ? await updateTask(id, payload)
        : await createTask(payload);

      const resData = response?.data;

      if (resData?.status === "success") {
        openSnackAlert(resData?.message, "success");
        navigate("/task-management");
      } else {
        openSnackAlert(
          resData?.message || `${isEditMode ? "Update" : "Create"} task failed`,
          "error"
        );
      }
    } catch (error) {
      console.error(error);
      openSnackAlert(error?.message || "Something went wrong", "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      bgcolor="#FAFAFA"
      padding={"24px"}
      borderRadius={"16px"}
      sx={{
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        maxWidth: "1200px",
        margin: "0 auto",
      }}
    >
      <AppLoader isVisible={isLoading} />
      <CustomHeading
        tittle={isEditMode ? "Edit Task" : "Task Management"}
        subtittle={
          isEditMode
            ? "Update task details and assignments"
            : "Assign and track all form of employees duties"
        }
      />
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} md={4} sx={{ ml: "auto" }}>
          <CustomSelect
            label="Task Type"
            name="taskCategory"
            value={form.taskCategory}
            onChange={handleTaskCategoryChange}
            sx={{ width: "100%", height: "36px" }}
            placeholder="Select Task Type"
          >
            <MenuItem value="snowRemoval">Snow Removal</MenuItem>
            <MenuItem value="landscape">Landscape</MenuItem>
          </CustomSelect>
        </Grid>

        {/* Row 1 */}
        <Grid item xs={12} md={8}>
          <CustomMultiSelect
            label="Select Workers"
            placeholder="Select Workers"
            value={form.employeeOrTeam}
            onChange={(values) =>
              handleMultiSelectChange("employeeOrTeam", values)
            }
            options={allWorkers.map((worker) => ({
              value: worker._id,
              label: `${worker.firstName} ${worker.lastName}`,
            }))}
            error={!!validationErrors.employeeOrTeam}
            errorMessage={validationErrors.employeeOrTeam}
            required
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <CustomSelect
            label="Select Task Type"
            name="taskType"
            value={form.taskType}
            onChange={handleChange}
            sx={{ width: "100%", height: "36px" }}
            placeholder="Select Task Type"
          >
            <MenuItem value="job">Job</MenuItem>
            <MenuItem value="activity">Activity</MenuItem>
            <MenuItem value="task">Task</MenuItem>
          </CustomSelect>
        </Grid>
        <Grid item xs={12} md={8}>
          <TextInput
            label="Task Title"
            name="title"
            value={form.title}
            onChange={handleChange}
            placeholder="Enter task title"
            sx={{ width: "100%", height: "36px" }}
            inputBgColor="#fff" 
            noBorder={true}
          />
        </Grid>
        {/* Row 2 */}
        <Grid item xs={12} md={4}>
          <CustomSelect
            label="Priority"
            name="priority"
            value={form.priority}
            onChange={handleChange}
            sx={{ width: "100%", height: "36px" }}
            placeholder="Select Priority"
          >
            <MenuItem value="high">High</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="low">Low</MenuItem>
          </CustomSelect>
        </Grid>
        <Grid item xs={12} md={4}>
          <CustomDatePicker
            label="Start Date"
            value={form.startDate}
            onChange={(date) => handleDateChange("startDate", date)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <CustomDatePicker
            label="End Date"
            value={form.endDate}
            onChange={(date) => handleDateChange("endDate", date)}
          />
        </Grid>
        {/* <Grid item xs={12} md={2} display="flex" alignItems="center">
          <CustomSwitch
            label="Notify Email"
            checked={form.notifyEmail}
            onChange={handleSwitch("notifyEmail")}
          />
        </Grid>
        <Grid item xs={12} md={2} display="flex" alignItems="center">
          <CustomSwitch
            label="Push Notification"
            checked={form.notifyPush}
            onChange={handleSwitch("notifyPush")}
          />
        </Grid> */}


                {/* Task Images Upload */}
                <Grid item xs={12} md={4}>
          <CustomInputLabel label="Task Images (Optional)" />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width: "100%",
            }}
          >
            {/* Upload Area */}
            <Box
              sx={{
                width: "100%",
                minHeight: 120,
                borderRadius: "8px",
                background: "#fff",
                border: "2px dashed #225B27",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                cursor: "pointer",
                "&:hover": {
                  background: "#F0F2F5",
                },
              }}
              component="label"
            >
              <Box sx={{ textAlign: "center" }}>
                <Typography sx={{ fontSize: 24, mb: 0.5 }}>📷</Typography>
                <Typography
                  sx={{ fontSize: 14, color: "#225B27", fontWeight: 500 }}
                >
                  Upload Task Images
                </Typography>
                <Typography sx={{ fontSize: 12, color: "#666" }}>
                  Max 10 images, 5MB each (.jpg, .png, .webp)
                </Typography>
              </Box>
              <input
                type="file"
                hidden
                multiple
                onChange={handleImageUpload}
                accept="image/jpeg,image/jpg,image/png,image/webp"
              />
            </Box>

            {/* Image Preview Grid */}
            {form.taskImages.length > 0 && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                  gap: 2,
                  mt: 2,
                }}
              >
                {form.taskImages.map((imageUrl, index) => (
                  <Box
                    key={index}
                    sx={{
                      position: "relative",
                      width: 120,
                      height: 120,
                      borderRadius: "8px",
                      overflow: "hidden",
                      border: "2px solid #e0e0e0",
                    }}
                  >
                    <img
                      src={imageUrl}
                      alt={`Task image ${index + 1}`}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                    <IconButton
                      onClick={() => handleRemoveImage(index)}
                      sx={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        width: 24,
                        height: 24,
                        background: "#ff4444",
                        color: "white",
                        "&:hover": {
                          background: "#cc0000",
                        },
                        "& .MuiSvgIcon-root": {
                          fontSize: 16,
                        },
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Grid>

        {form.taskCategory === "snowRemoval" && (
          <Grid item xs={12} md={4}>
            <CustomInputLabel label="Excel File Attachment (Optional)" />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                width: "100%",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: 120,
                  borderRadius: "8px",
                  background: "#F8F9FA",
                  border: "2px dashed #225B27",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  cursor: "pointer",
                  "&:hover": {
                    background: "#F0F2F5",
                  },
                }}
                component="label"
              >
                {form.attachments ? (
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      sx={{ fontSize: 12, color: "#225B27", fontWeight: 500 }}
                    >
                      📊 Excel File Uploaded
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: "#666", mt: 0.5 }}>
                      Click to change
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: "center" }}>
                    <Typography sx={{ fontSize: 24, mb: 0.5 }}>📊</Typography>
                    <Typography
                      sx={{ fontSize: 12, color: "#225B27", fontWeight: 500 }}
                    >
                      Upload Excel File
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: "#666" }}>
                      Max 10MB (.xls, .xlsx)
                    </Typography>
                  </Box>
                )}
                <input
                  type="file"
                  hidden
                  onChange={handleChangeFile}
                  accept=".xls,.xlsx"
                />
                {form.attachments && (
                  <IconButton
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRemoveFile();
                    }}
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      width: 24,
                      height: 24,
                      background: "#ff4444",
                      color: "white",
                      "&:hover": {
                        background: "#cc0000",
                      },
                      "& .MuiSvgIcon-root": {
                        fontSize: 16,
                      },
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                )}
              </Box>
              {form.attachments && (
                <Typography
                  sx={{ fontSize: 10, color: "#666", textAlign: "center" }}
                >
                  Excel files only (.xls, .xlsx)
                </Typography>
              )}
            </Box>
          </Grid>
        )}

<Grid item xs={12} md={4}>
          <TextInput
            label="Task Description"
            name="description"
            value={form.description}
            onChange={handleChange}
            placeholder="Enter task description"
            multiline
            rows={5}
            inputBgColor="#fff" 
            noBorder={true}
          />
        </Grid>

        {form.taskCategory === "landscape" && (
          <>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 2,
                }}
              >
                {/* <CustomInputLabel label="Material Options" /> */}
                <IconButton
                  onClick={handleAddMaterialRow}
                  sx={{
                    color: "#225B27",
                    border: "1px solid #225B27",
                    borderRadius: "8px",
                    width: 36,
                    height: 36,
                    "&:hover": { background: "rgba(34, 91, 39, 0.08)" },
                  }}
                  aria-label="Add material row"
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>
              {validationErrors.materialOption && (
                <Typography sx={{ fontSize: 12, color: "#d32f2f", mt: 0.5 }}>
                  {validationErrors.materialOption}
                </Typography>
              )}
            </Grid>
            {form.materialOption.map((row, index) => (
              <Grid item xs={12} md={12} key={`material-${index}`}>
                {/* <CustomInputLabel
                  label={index === 0 ? "Material" : `Material ${index + 1}`}
                /> */}
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <TextInput
                    placeholder="Type"
                    value={row.type || ""}
                    onChange={(e) =>
                      handleMaterialOptionChange(index, "type", e.target.value)
                    }
                    sx={{ width: "32%", height: "36px" }}
                    inputBgColor="#fff"
                    noBorder={true}
                  />
                  <TextInput
                    placeholder="Amount"
                    value={row.amount || ""}
                    onChange={(e) =>
                      handleMaterialOptionChange(
                        index,
                        "amount",
                        e.target.value
                      )
                    }
                    sx={{ width: "32%", height: "36px" }}
                    inputBgColor="#fff"
                    noBorder={true}
                  />
                  <TextInput
                    placeholder="Note"
                    value={row.note || ""}
                    onChange={(e) =>
                      handleMaterialOptionChange(index, "note", e.target.value)
                    }
                    sx={{ width: "32%", height: "36px" }}
                    inputBgColor="#fff"
                    noBorder={true}
                  />
                  {form.materialOption.length > 1 && (
                    <IconButton
                      onClick={() => handleRemoveMaterialRow(index)}
                      sx={{
                        width: 28,
                        height: 28,
                        color: "#ff4444",
                        flexShrink: 0,
                        "&:hover": { background: "rgba(255, 68, 68, 0.08)" },
                      }}
                      aria-label="Remove material row"
                    >
                      <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  )}
                </Box>
              </Grid>
            ))}
          </>
        )}

        {/* Row 3 */}





        {/* Buttons */}
        <Grid
          item
          xs={12}
          md={12}
          display="flex"
          justifyContent="center"
          mt={4}
        >
          <CustomButton
            btnLabel={isEditMode ? "Update" : "Save"}
            handlePressBtn={handleSubmit}
            btnBgColor="#fff"
            btnTextColor="#225B27"
            borderColor="#225B27"
            isBorder="1px solid #225B27"
            width={120}
            sx={{ marginRight: 2 }}
          />
          <CustomButton
            btnLabel="Cancel"
            handlePressBtn={() => navigate("/task-management")}
            btnBgColor="#A0A0A0"
            btnTextColor="#fff"
            width={120}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default AddEditTask;
